/**
 * Query keys — used by sources internally and by renderers for cache invalidation.
 * Follows the factory pattern (hierarchical, identity-scoped, partial-match friendly).
 */
export const activityQueryKeys = {
    /** Matches all activity queries — use for broad invalidation. */
    all: ["activities"] as const,
    /** Workspace invites for a user. Scoped by userId (invites are cross-workspace). */
    invites: (userId: string) => [...activityQueryKeys.all, "invites", userId] as const,
    /** Join requests across a user's joinable workspaces. Scoped by userId. */
    joinRequests: (userId: string) => [...activityQueryKeys.all, "joinRequests", userId] as const,
    /**
     * Pending join requests for a single workspace — fetched per joinable workspace by
     * `JoinRequestSource`. Separate from `joinRequests(userId)` so a single workspace's
     * cache can be invalidated on mutation without refetching the whole fan-out.
     */
    joinRequestsByWorkspace: (workspaceId: string) =>
        [...activityQueryKeys.all, "joinRequests", "workspace", workspaceId] as const
};

/** Lifecycle state of a background activity. */
export enum ActivityStatus {
    Pending = "pending",
    Running = "running",
    Completed = "completed",
    Failed = "failed"
}

/**
 * Mirrors the backend's `async_tasks.status` field (see `app/functions/asyncTasks/models.js`).
 * The frontend normalizes these into `ActivityStatus` via `toStatusEnum`.
 */
export enum AsyncTaskStatus {
    Created = "created",
    Running = "running",
    Completed = "completed",
    Failed = "failed"
}

/**
 * Known `async_tasks.taskName` values the Activity Center renders. Adding a new
 * async task type means: register it in `sources/asyncTaskMappings.ts` *and*
 * add the entry here so call sites can switch on a typed identifier.
 */
export enum AsyncTaskName {
    RemapClasses = "remapClasses",
    MergeDatasets = "mergeDatasets",
    CopyProjectImages = "copyProjectImages",
    ExportDataset = "exportDataset"
}

/**
 * UI renderer key — determines which registered renderer handles this item.
 *
 * Superset of ActivityEventKind: includes kinds that come from Firestore events
 * (AsyncTask, CIModelChange) plus kinds synthesized client-side (Invite, JoinRequest).
 */
export enum ActivityUIKind {
    AsyncTask = "async_task",
    CIModelChange = "continuous_improvement_model_change",
    Invite = "invite",
    JoinRequest = "join_request"
}
